import { Card } from './ui/card';
import { Progress } from './ui/progress';
import { CheckCircle2, Circle, Loader2 } from 'lucide-react';
import type { ProductionStatus } from '@/types';
import { cn } from '@/lib/utils';

interface ProductionProgressProps {
  status: ProductionStatus;
  progress: number;
  currentStep?: string;
}

const PRODUCTION_STEPS = [
  { status: 'IDLE', label: 'Ready', description: 'Waiting for input' },
  { status: 'CROPPING', label: 'Cropping', description: 'Preparing print files' },
  { status: 'UPLOADING', label: 'Uploading', description: 'Dual upload to Drive + Bunny' },
  { status: 'GENERATING_PDF', label: 'PDF Generation', description: 'Creating delivery document' },
  { status: 'AWAITING_EXTERNAL_MOCKUPS', label: 'Mockups', description: 'Generating room scenes' },
  { status: 'SAVING', label: 'Saving', description: 'Storing to database' },
  { status: 'COMPLETED', label: 'Complete', description: 'Ready for export' },
] as const;

export function ProductionProgress({ status, progress, currentStep }: ProductionProgressProps) {
  const currentStepIndex = PRODUCTION_STEPS.findIndex(step => step.status === status);

  return (
    <Card className="p-6">
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h3 className="font-semibold text-lg">Production Line Status</h3>
          <p className="text-sm text-muted-foreground mt-1">
            {currentStep || PRODUCTION_STEPS[currentStepIndex]?.description || 'Processing...'}
          </p>
        </div>

        {/* Progress Bar */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="font-medium">{Math.round(progress)}%</span>
            <span className="text-muted-foreground">
              {status === 'COMPLETED' ? 'Complete' : 'In Progress'}
            </span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Steps */}
        <div className="space-y-3">
          {PRODUCTION_STEPS.map((step, index) => {
            const isComplete = index < currentStepIndex;
            const isCurrent = index === currentStepIndex;
            const isError = status === 'ERROR' && isCurrent;

            return (
              <div
                key={step.status}
                className={cn(
                  'flex items-start gap-3 transition-opacity',
                  !isComplete && !isCurrent && 'opacity-40'
                )}
              >
                <div className="mt-0.5">
                  {isComplete ? (
                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                  ) : isCurrent ? (
                    isError ? (
                      <Circle className="h-5 w-5 text-destructive" />
                    ) : (
                      <Loader2 className="h-5 w-5 text-primary animate-spin" />
                    )
                  ) : (
                    <Circle className="h-5 w-5 text-muted-foreground" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={cn(
                    'font-medium text-sm',
                    isCurrent && 'text-primary',
                    isError && 'text-destructive'
                  )}>
                    {step.label}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {step.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Error State */}
        {status === 'ERROR' && (
          <div className="rounded-lg bg-destructive/10 border border-destructive/20 p-4">
            <p className="text-sm font-medium text-destructive">
              Production Error
            </p>
            <p className="text-xs text-destructive/80 mt-1">
              {currentStep || 'An error occurred during production'}
            </p>
          </div>
        )}
      </div>
    </Card>
  );
}

